import React, { Fragment, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Avatar, Badge, Box, Collapse, Divider, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import Navigations from 'Navigation';
import Colors from 'assets/style/Colors';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useAuth } from 'context/UseContext';
import { usePusher } from 'hooks/usePusher';

function Nav() {

  const { notifications } = usePusher();
  const { user } = useAuth();
  const { pathname } = useLocation();

  const [expand, setExpand] = useState([]);

  // *For Collapse
  const handleCollapse = (value) => {
    const currentIndex = expand.indexOf(value);
    const newExpand = [...expand];

    if (currentIndex === -1) {
      newExpand.push(value);
    } else {
      newExpand.splice(currentIndex, 1);
    }

    setExpand(newExpand);
  };

  return (
    <Box sx={{ bgcolor: Colors.tertiary, boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)', height: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', pt: 4, pb: 2, px: 2 }}>
        <Avatar
          src={user?.image}
          sx={{
            mr: 1,
            width: 40,
            height: 40,
            border: `1px solid ${Colors.primary}`,
            '.MuiAvatar-img': { objectFit: 'contain' }
          }}
        />
        <Box sx={{ width: 1 }}>
          <Typography variant="subtitle1" className="text-truncate" sx={{ color: Colors.white }}>
            {user?.username ? user?.username : user?.name}
          </Typography>
          <Typography variant="body2" sx={{ color: Colors.grey }}>
            {user?.email ? user?.email : user?.email}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ width: '80%', mx: 'auto', my: 1 }} />
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <List>
          {Navigations.map((item, index) => {
            const isSelected = item.path === pathname ? true : false
            const isAlert = notifications.some(e => item?.alertTypes?.includes(e?.type) && e?.seen === 0)
            return (
              <Fragment key={index}>
                <ListItemButton key={index}
                  component={item.path ? Link : 'div'}
                  to={item.path ?? ''}
                  onClick={() => item?.children?.length > 0 && handleCollapse(item.label)}
                  sx={{
                    mt: 1.5,
                    background: isSelected ? Colors.charcoalBlack : "",
                    ".MuiSvgIcon-root": {
                      color: isSelected ? Colors.white : Colors.textSecondary,
                    },
                    '&:hover': {
                      background: Colors.charcoalBlack,
                      ".MuiSvgIcon-root": {
                        color: Colors.white
                      },
                      ".MuiTypography-root": {
                        color: Colors.white
                      },
                    }
                  }}
                >
                  <Badge variant="dot" invisible={!isAlert} color="danger" anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}>
                    <ListItemIcon sx={{ minWidth: '35px' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1"
                          sx={{
                            color: isSelected ? Colors.white : Colors.textSecondary,
                            fontWeight: isSelected ? 'bold' : '500'
                          }}>
                          {item.label}
                        </Typography>
                      }
                    />
                    {item?.children &&
                      (expand.indexOf(item.label) !== -1 ?
                        <ExpandLess sx={{ color: Colors.textSecondary }} /> : <ExpandMore sx={{ color: Colors.textSecondary }} />)
                    }
                  </Badge>
                </ListItemButton>
                {item?.children &&
                  <Collapse in={expand.indexOf(item.label) !== -1} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.children.map((child, i) => {
                        const isChildAlert = notifications.some(e => child?.alertTypes.includes(e?.type) && e?.seen === 0)
                        return (
                          <ListItemButton key={i}
                            component={child.path ? Link : 'div'}
                            to={child.path}
                            sx={{
                              mt: 0.5,
                              pl: '50px',
                              background: child.path === pathname ? Colors.charcoalBlack : "",
                              ".MuiSvgIcon-root": {
                                color: child.path === pathname ? Colors.white : Colors.textSecondary,
                              },
                              '&:hover': {
                                background: Colors.charcoalBlack,
                                ".MuiSvgIcon-root": {
                                  color: Colors.white
                                },
                                ".MuiTypography-root": {
                                  color: Colors.white
                                },
                              }
                            }}
                          >
                            <Badge variant="dot" invisible={!isChildAlert} color="danger" anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}>
                              <ListItemIcon sx={{ minWidth: 30, '.MuiSvgIcon-root': { fontSize: "16px" } }}>
                                {child.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography type="body2"
                                    sx={{
                                      fontSize: 13,
                                      fontWeight: child.path === pathname ? "bold" : "normal",
                                      color: child.path === pathname ? Colors.white : Colors.textSecondary
                                    }}>
                                    {child.label}
                                  </Typography>
                                }
                              />
                            </Badge>
                          </ListItemButton>
                        )
                      })}
                    </List>
                  </Collapse>
                }
              </Fragment>
            )
          })}
        </List>
      </Box>
    </Box>
  )
}

function Sidebar({ mobileOpen, onMobileClose }) {

  const { pathname } = useLocation();

  useEffect(() => {
    if (mobileOpen) {
      onMobileClose();
    }
  }, [pathname]);

  return (
    <Fragment>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: 280, boxSizing: 'border-box' },
        }}
      >
        <Nav />
      </Drawer>

      {/* Desktop permanent sidebar */}
      <Box sx={{ display: { xs: 'none', md: 'block' }, width: 320, flexShrink: 0, height: 'calc(100vh - 104px)' }}>
        <Nav />
      </Box>

    </Fragment>
  );
}

export default Sidebar;